import React, { useState, useEffect } from "react";
import { FaBars, FaSearch, FaTimes } from "react-icons/fa";
import { Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { allAPiServicesCall } from "../../services/apiServices";
import { getAuthConfig } from "../../services/apiUtils";
import { artistImageUrl } from "../../utils/utils";
import "./Topbar.css";

const Topbar = ({ onToggleSidebar, isSmallMobile, isMobileView, isSidebarCollapsed }) => {
  const [adminData, setAdminData] = useState({});
  const [songs, setSongs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const navigate = useNavigate();

  const getDetails = async () => {
    // First load from localStorage for immediate display
    const details = JSON.parse(localStorage.getItem("admin-details"));
    setAdminData(details || {});

    // Then fetch fresh data from API
    try {
      const response = await allAPiServicesCall.getProfileDetails(
        {},
        getAuthConfig(),
        navigate
      );
      if (response?.success && response?.data) {
        const rawData = response.data;
        const data = rawData._doc || rawData;
        setAdminData(data);
        // Update localStorage with fresh data
        const currentDetails = JSON.parse(localStorage.getItem("admin-details")) || {};
        localStorage.setItem("admin-details", JSON.stringify({
          ...currentDetails,
          ...data
        }));
      }
    } catch (error) {
      console.error("Error fetching profile for topbar:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin-details");
    localStorage.removeItem("auth-token");
    setAdminData({});
    navigate("/login");
  };

  const handleSearch = async (term) => {
    if (!term) return setSongs([]); // Clear results if search term is empty
    try {
      const response = await allAPiServicesCall.getSearchSong(
        { song_name: term },
        getAuthConfig()
      );
      if (response.success) {
        setSongs(response.data);
      } else {
        setSongs([]);
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
    }
  };

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (debounceTimeout) clearTimeout(debounceTimeout); // Clear previous timeout
    setDebounceTimeout(setTimeout(() => handleSearch(term), 300)); // Set new timeout
  };

  const handleSongClick = (songId) => {
    setSongs([]); // Clear search results
    setSearchTerm(""); // Clear search term
    navigate(`/lyrics/${songId}`);
  };

  useEffect(() => {
    getDetails();
  }, []);

  // Clear search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.querySelector('.topbar-search-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        setSongs([]);
        setSearchTerm("");
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeMobileSearch = () => {
    setShowMobileSearch(false);
    setSongs([]);
    setSearchTerm("");
  };

  // Render search bar component
  const renderSearchBar = () => (
    <div className="input-group">
      <input
        type="search"
        className="form-control topbar-search-input"
        placeholder="Search for a song..."
        aria-label="Search"
        value={searchTerm}
        onChange={handleInputChange}
        autoFocus={showMobileSearch}
      />
      {showMobileSearch && (
        <button
          className="btn btn-outline-secondary"
          type="button"
          onClick={closeMobileSearch}
          aria-label="Close search"
        >
          <FaTimes />
        </button>
      )}
    </div>
  );

  // Render search results
  const renderSearchResults = () => (
    songs.length > 0 && (
      <ul className="topbar-search-results list-group position-absolute">
        {songs.map((song) => (
          <li
            key={song._id}
            className="list-group-item topbar-search-result-item d-flex align-items-center"
            onClick={() => {
              handleSongClick(song.song_id);
              if (showMobileSearch) closeMobileSearch();
            }}
          >
            <img
              src={artistImageUrl(song?.artist)}
              alt={song.song_name}
              width="48"
              height="48"
              className="me-3 rounded"
            />
            <div className="flex-grow-1 min-width-0">
              <h6 className="mb-0 text-truncate">{song?.song_name}</h6>
              <p className="mb-0 text-muted small text-truncate">
                {song?.artist?.artist_name}
              </p>
            </div>
          </li>
        ))}
      </ul>
    )
  );

  return (
    <>
      {/* Mobile Search Overlay */}
      {showMobileSearch && (
        <div className="topbar-mobile-search-overlay">
          <div className="topbar-mobile-search-container">
            {renderSearchBar()}
            {renderSearchResults()}
          </div>
        </div>
      )}

      <nav
        className="topbar navbar navbar-light w-100"
        style={{
          left: !isMobileView && !isSidebarCollapsed ? 'var(--sidebar-width-desktop)' : '0',
          width: !isMobileView && !isSidebarCollapsed ? 'calc(100% - var(--sidebar-width-desktop))' : '100%',
          transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div className="container-fluid d-flex align-items-center">
          {/* Sidebar Toggle Icon */}
          <button
            className={`topbar-toggle-btn btn btn-link p-2 ${isSidebarCollapsed && !isMobileView ? 'sidebar-hidden' : ''}`}
            onClick={onToggleSidebar}
            aria-label={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
            title={isMobileView ? "Open menu" : (isSidebarCollapsed ? "Show sidebar" : "Hide sidebar")}
          >
            {isMobileView ? (
              <FaBars size={20} />
            ) : (
              isSidebarCollapsed ? <FaBars size={20} /> : <FaTimes size={20} />
            )}
          </button>

          {/* Search Bar - Hidden on small mobile, shown on larger screens */}
          {!isSmallMobile ? (
            <div className="topbar-search-container mx-auto position-relative">
              {renderSearchBar()}
              {renderSearchResults()}
            </div>
          ) : (
            /* Search Icon Button - Shown only on small mobile */
            <button
              className="topbar-search-btn btn btn-link p-2 text-dark ms-auto me-2"
              onClick={() => setShowMobileSearch(true)}
              aria-label="Open search"
            >
              <FaSearch size={18} />
            </button>
          )}

          {/* User Dropdown */}
          <div className={isSmallMobile ? "" : "ms-3"}>
            <Dropdown align="end">
              <Dropdown.Toggle
                as="div"
                className="topbar-user-toggle d-flex align-items-center"
              >
                <img
                  src={adminData?.avatar || "/default-avatar.png"}
                  alt="User"
                  className="rounded-circle"
                  width="40"
                  height="40"
                />
              </Dropdown.Toggle>

              <Dropdown.Menu className="topbar-user-menu">
                <Dropdown.Item onClick={() => navigate("/profile")}>
                  View Profile
                </Dropdown.Item>
                <Dropdown.Item onClick={() => navigate("/change-password")}>
                  Change Password
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Topbar;
